import { Fragment } from 'react';
import { G, Path } from '@react-pdf/renderer';
import { ceil, clamp, mapValues, round } from 'lodash';

/**
* @function Ceiling
* @description Renders ceiling tile grid patterns in a PDF document with 
* support for different tile sizes, layouts, and markings.
* 
* @param {Object} props
* @param {string} props.areaId - Area ID to render ceiling for
* @param {number} props.scale - Scale factor for dimensions
* @param {Object} props.bbox - Bounding box dimensions
* @param {Object} props.content - Room configuration data
* @param {Object} props.content.areas - Areas data by ID
* @param {Object} props.constants - Configuration constants
* @param {Object} props.constants.ceilingTiles - Ceiling tile dimensions
* 
* @description
* Generates ceiling grid by:
* 1. Calculating maximum tile quantities based on room size
* 2. Handling different tile shapes (square, rectangular)
* 3. Supporting grid transformations and offsets
* 4. Adding tile markings for selected/checked tiles
* 5. Drawing grid lines and crosses for marked tiles
* 
* @example
* <Ceiling
*   areaId="room1"
*   scale={0.5}
*   content={{
*     areas: {
*       room1: {
*         ceilingTiles: {
*           tile: "2x2",
*           qty: {x: 10, y: 8},
*           checked: [{x: 2, y: 3, mask: 0}],
*           transform: {x: 0, y: 0}
*         },
*         dimensions: {length: 20, width: 16}
*       }
*     }
*   }}
* />
* 
* @note
* - Uses @react-pdf/renderer for PDF output
* - Supports different tile sizes from constants
* - Handles grid overflow with additional tiles
* - Supports tile marking patterns based on shape
* - Automatically centers grid in area
* - Maintains crisp grid lines with shapeRendering
*/
const Ceiling = (props) => {
  const { areaId, scale, bbox, content: { areas }, constants: { ceilingTiles } } = props;
  const { ceilingTiles: { checked, qty, tile, transform }, dimensions: { length, width } } = areas[areaId];
  const tileSize = mapValues(ceilingTiles[tile], (v) => v * scale);

  // Calculate the maximum number of tiles that fit in the room 
  const maxX = ceil(length * scale / tileSize.x);
  const maxY = ceil(width * scale / tileSize.y);

  // Check that qty is within max
  let { x = 0, y = 0 } = qty;
  x = x ? clamp(x, Math.min(1, maxX), maxX) : qty.x > 0 ? maxX : 0;
  y = y ? clamp(y, Math.min(1, maxY), maxY) : qty.y > 0 ? maxY : 0;

  // Set draw quantitites. If using full tile coverage, adds 1 tile to the perimeter to the grid can be moved
  const drawX = x + (x && x === maxX ? 2 : 0);
  const drawY = y + (y && y === maxY ? 2 : 0);

  // get the center of the tile arrray
  const cx = round((drawX * tileSize.x) * 2) / 4;
  const cy = round((drawY * tileSize.y) * 2) / 4;

  // Set the transformation
  const tx = (bbox.width / 2) - cx + transform.x * scale;
  const ty = (bbox.height / 2) - cy + transform.y * scale;

  // create the lines array
  const lines = [...Array(drawX + 1)].map((_, x) => {
    const x0 = round(tileSize.x * x * 2) / 2;
    const y1 = round((drawY * tileSize.y) * 2) / 2;
    return { d: `m ${x0} 0 v${y1}` }
  });
  lines.push(...[...Array(drawY + 1)].map((_, y) => {
    const y0 = round(tileSize.y * y * 2) / 2;
    const x1 = round((drawX * tileSize.x) * 2) / 2;
    return { d: `m 0 ${y0} h${x1}` }
  }))

  // Add the tile crosses
  const tileShape = Math.sign(tileSize.x - tileSize.y); // returns -1 for vertical, 0 for square and 1 for horizontal

  checked.forEach((ck, id) => {
    const x = 2 + ck.x * tileSize.x;
    const y = 2 + ck.y * tileSize.y;
    const side = round((Math.min(tileSize.x, tileSize.y) - 4) * 2) / 2;
    if (tileShape === 0 || ck.mask <= 0) lines.push({ d: `m ${x} ${y} l ${side} ${side} m 0 -${side} l -${side} ${side}` });
    if (tileShape !== 0 && ck.mask >= 0) lines.push({ d: `m ${x + (tileShape > 0 ? (side + 2) : 0)} ${y + (tileShape < 0 ? (side + 4) : 0)} l ${side} ${side} m 0 -${side} l -${side} ${side}` });
  });

  return (
    <Fragment>
      <G transform={`translate(${round(tx)}, ${round(ty)})`} >
        {lines.map((line, index) => <Path key={`line-${index}`} {...line} stroke="#74777a" strokeWidth={.5} shapeRendering={'crispEdges'} />)}
      </G>
    </Fragment>
  )
};

export default Ceiling;